import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSearch, FiSlash } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite, useAdminAccess } from '../utilities/adminAccess';
import { champFields } from '../utilities/tournamentsApi';
import { getPlayerPhotoUrl } from '../utilities/playersApi';
import {
  BLACKLIST_PAGE_SIZE,
  blacklistComment,
  blacklistDisplayName,
  blacklistPlayerId,
  extractBlacklistPage,
  formatBlacklistDate,
} from '../utilities/blacklistApi';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import defaultPlayerImage from '../images/defaultAvatar.png';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/blackList.css';

export default function BlackList() {
  const dispatch = useDispatch();
  const { t, lang } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canReadChamp } = useAdminAccess('tournaments');

  const [players, setPlayers] = useState([]);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const searchTimer = useRef(null);
  const requestIdRef = useRef(0);
  const playersRef = useRef([]);
  playersRef.current = players;

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!champId || !canReadChamp) {
      setPlayers([]);
      setTotal(null);
      setHasMore(false);
      setLoading(false);
      setMessage({
        text: champsLoading ? '' : t(!champId ? 'championshipsLoadError' : 'champNoAccess'),
        type: champsLoading ? '' : 'error',
      });
      return undefined;
    }
    const delay = searchQuery ? 350 : 0;
    searchTimer.current = setTimeout(() => {
      loadList(searchQuery, 0, false);
    }, delay);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery, champId, champsLoading, canReadChamp]);

  const loadList = async (search, offset, append) => {
    const requestId = ++requestIdRef.current;
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setMessage({ text: '', type: '' });
    }

    try {
      const body = {
        ...champFields(champId),
        limit: BLACKLIST_PAGE_SIZE,
        offset,
      };
      if (search && search.trim()) body.search = search.trim();
      const response = await authorizedPost(dispatch, API_URLS.getAdminBlackList, body);
      if (requestId !== requestIdRef.current) return;

      const page = extractBlacklistPage(response.data);
      const nextPlayers = append ? playersRef.current.concat(page.items) : page.items;
      setPlayers(nextPlayers);
      setTotal(page.total);
      setHasMore(
        page.total != null
          ? nextPlayers.length < page.total
          : page.items.length === BLACKLIST_PAGE_SIZE
      );
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      if (!append) {
        setPlayers([]);
        setTotal(null);
        setHasMore(false);
      }
      setMessage({ text: getApiErrorMessage(error, t('blLoadError')), type: 'error' });
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const columns = [
    {
      key: 'player',
      label: t('blPlayer'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="bl-player-cell">
          <img
            src={getPlayerPhotoUrl(item.image) || defaultPlayerImage}
            alt=""
            onError={(event) => { event.currentTarget.src = defaultPlayerImage; }}
          />
          <span>{blacklistDisplayName(item) || '—'}</span>
        </span>
      ),
    },
    {
      key: 'date',
      label: t('blDate'),
      className: 'bl-cell-date st-cell-muted',
      render: (item) => formatBlacklistDate(item.blacklistedTimestamp, lang) || t('blNotSet'),
    },
    {
      key: 'comment',
      label: t('blReason'),
      className: 'bl-cell-comment st-cell-muted',
      render: (item) => (
        <span className="bl-comment-text">{blacklistComment(item) || t('blNotSet')}</span>
      ),
    },
    {
      key: 'by',
      label: t('blAddedBy'),
      className: 'bl-cell-by st-cell-muted hide-sm',
      render: (item) => item.blacklistedBy_name || '—',
    },
  ];

  const foundCount = total != null ? total : players.length;

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page bl-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navBlackList') }]} />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('blSearchPh')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            ) : null}
          </div>
          <div className="st-meta">
            <strong>{t('blFound', { count: foundCount })}</strong>
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-table-wrap">
            {loading ? (
              <div className="st-loading">
                <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                <p>{t('blLoading')}</p>
              </div>
            ) : (
              <>
                <ReorderableTable
                  tableClassName="st-table-blacklist"
                  columns={columns}
                  items={players}
                  getRowKey={(item, index) => blacklistPlayerId(item) || index}
                  reorderMode={false}
                  onItemsChange={() => {}}
                  getRowHref={(item) => {
                    const id = blacklistPlayerId(item);
                    return id ? `/black_list/${id}` : null;
                  }}
                  empty={(
                    <div className="st-empty">
                      <FiSlash size={48} />
                      <h3>{t('blEmpty')}</h3>
                      <p>{t('blEmptyHint')}</p>
                    </div>
                  )}
                />
                {hasMore ? (
                  <div className="bl-load-more">
                    <button
                      type="button"
                      className="st-btn st-btn-secondary"
                      disabled={loadingMore}
                      onClick={() => loadList(searchQuery, players.length, true)}
                    >
                      {loadingMore ? t('blLoading') : t('dqLoadMore')}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        <div className="st-actions">
          <IfCanWrite entity="tournaments">
            <Link to="/black_list/new" className="st-btn st-btn-primary">
              <FiPlus />
              <span className="tr-btn-full">{t('blAdd')}</span>
              <span className="tr-btn-short">{t('blAddShort')}</span>
            </Link>
          </IfCanWrite>
        </div>
      </div>
    </div>
  );
}
